import React, { useState, useCallback } from "react";
import { Container, Box, Typography, Snackbar, Alert, Button } from "@mui/material";
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import { useQuery, useMutation } from "@tanstack/react-query";
import { fetchServices, getCurrentBusinessConfig, updateEmployee } from "../../../firebase/firebase-delegate";
import { BusinessConfig, Employee, Service } from "../../../types";
import MyServicesTable from "./MyServicesTable";
import ContactCard from "./ContactCard";
import { tenantStoragePath } from "../../../tenancy/paths";
import { useTenant } from "../../../tenancy/TenantContext";
import LoadingSpinner from "../../../components/global/LoadingSpinner";
import { useAdminData } from "../../../contexts/AdminDataContext";
import { useQueryClient } from "@tanstack/react-query";
import { EMPLOYEE_BIO_MAX_LENGTH } from "../../../constants";
import { resolveAdminBookingUxConfig } from "../../../utils/bookingUxConfig";
import { createEmployeeBookingUrl, getTenantPublicOrigin } from "../../../sitePublishing/publicConfigSource";


const MyProfileScreen: React.FC = () => {
    const { tenantId } = useTenant();
    const [profileImageRevision, setProfileImageRevision] = useState(0);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success',
    });


    // Fetch current employee data
    const {
        currentEmployeeQuery: employeeQuery,
        hydrateAppointmentsQuery,
        scheduleQuery,
        workHoursQuery,
    } = useAdminData();

    // Fetch all services data
    const servicesQuery = useQuery<Service[]>({
        queryKey: ['services', tenantId],
        queryFn: () => fetchServices(tenantId),
    });
    const businessConfigQuery = useQuery<BusinessConfig | null>({
        queryKey: ['businessConfig', tenantId],
        queryFn: () => getCurrentBusinessConfig(tenantId),
    });
    const bookingUxConfig = resolveAdminBookingUxConfig(businessConfigQuery.data);
    const isResourceMode = bookingUxConfig.bookingModel === 'resource';

    // Fetch the next 7 days work hours
    // This query will fetch the work hours for the next 7 days based on the employee's schedule
    // and the current date. It will return an array of objects containing the date, day, and hours information.
    // The query will be enabled only if the employee ID and schedule data are available.
    // Work hours and next 7 days tables intentionally hidden (UI-only request)


    const queryClient = useQueryClient();
    const updateEmployeeMutation = useMutation({
        mutationFn: async ({ nextEmployee, imageFile }: { nextEmployee: Employee; imageFile?: File }) => {
            if (!employeeQuery.data) {
                throw new Error("Employee data is missing");
            }
            await updateEmployee(employeeQuery.data, nextEmployee, imageFile, tenantId);
            return {
                nextEmployee,
                imageChanged: Boolean(imageFile) ||
                    !nextEmployee.profilePictureImagePath !== !employeeQuery.data.profilePictureImagePath,
            };
        },
        onSuccess: ({ nextEmployee, imageChanged }) => {
            if (nextEmployee) {
                queryClient.setQueryData(['currentEmployee', tenantId], nextEmployee);
            }
            if (imageChanged) setProfileImageRevision((current) => current + 1);
            setSnackbar({ open: true, message: 'Changes saved successfully.', severity: 'success' });
        },
        onError: (error: Error) => {
            console.error("Error updating employee: ", error);
            setSnackbar({ open: true, message: error.message || 'Failed to save changes.', severity: 'error' });
        }
    });

    const derivedScheduleLoading =
        scheduleQuery.isLoading ||
        scheduleQuery.isFetching ||
        hydrateAppointmentsQuery.isLoading ||
        hydrateAppointmentsQuery.isFetching;

    const isLoading =
        employeeQuery.isLoading ||
        servicesQuery.isLoading ||
        businessConfigQuery.isLoading ||
        workHoursQuery.isLoading ||
        derivedScheduleLoading;
    const isError =
        employeeQuery.isError ||
        servicesQuery.isError ||
        businessConfigQuery.isError ||
        workHoursQuery.isError ||
        scheduleQuery.isError ||
        hydrateAppointmentsQuery.isError;

    // Work hours and next 7 days tables intentionally hidden (UI-only request)

    const handleCopyBookNowLink = useCallback(async () => {
        if (!employeeQuery.data?.id) {
            setSnackbar({ open: true, message: 'Unable to generate your booking link.', severity: 'error' });
            return;
        }

        try {
            const publicOrigin = await getTenantPublicOrigin(tenantId);
            const bookNowLink = createEmployeeBookingUrl(publicOrigin, tenantId, employeeQuery.data.id);
            await navigator.clipboard.writeText(bookNowLink);
            setSnackbar({ open: true, message: 'Book now link copied.', severity: 'success' });
        } catch (error) {
            console.error('Failed to copy booking link:', error);
            setSnackbar({ open: true, message: 'Failed to copy book now link.', severity: 'error' });
        }
    }, [employeeQuery.data?.id, tenantId]);


    if (isLoading) {
        return (
            <Container sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
                <LoadingSpinner />
            </Container>
        );
    }

    if (!employeeQuery.data || !servicesQuery.data || isError) {
        return <Typography variant="h6" align="center">Error loading profile</Typography>;
    }

    // Filter services that include the current employee ID
    const assignedServices = servicesQuery.data.filter(service => service.employees.includes(employeeQuery.data.id));

    const onSaveContactInfo = (
        updatedFirstName: string,
        updatedLastName: string,
        updatedEmail: string,
        updatedPhone: string,
        updatedBio: string,
        updatedSocials: { instagram?: string; facebook?: string; twitter?: string },
        imageFile: File | undefined,
        removeImage: boolean
    ): Promise<void> => {
        if (employeeQuery.data) {
                const nextEmployee: Employee = {
                    ...employeeQuery.data,
                    firstName: updatedFirstName,
                    lastName: updatedLastName,
                    email: updatedEmail,
                    phone: updatedPhone,
                };
                const trimmedBio = updatedBio.trim().slice(0, EMPLOYEE_BIO_MAX_LENGTH);
                if (trimmedBio.length === 0) {
                    delete nextEmployee.bio;
                } else {
                    nextEmployee.bio = trimmedBio;
                }
                if (!updatedSocials.instagram && !updatedSocials.facebook && !updatedSocials.twitter) {
                    delete nextEmployee.socials;
                } else {
                    const sanitizedSocials: { instagram?: string; facebook?: string; twitter?: string } = {};
                    if (updatedSocials.instagram) {
                        sanitizedSocials.instagram = updatedSocials.instagram;
                    }
                    if (updatedSocials.facebook) {
                        sanitizedSocials.facebook = updatedSocials.facebook;
                    }
                    if (updatedSocials.twitter) {
                        sanitizedSocials.twitter = updatedSocials.twitter;
                    }
                    nextEmployee.socials = sanitizedSocials;
                }
                if (removeImage) {
                    delete nextEmployee.profilePictureImagePath;
                } else if (imageFile && !nextEmployee.profilePictureImagePath) {
                    nextEmployee.profilePictureImagePath = tenantStoragePath(
                        tenantId,
                        `employees/${employeeQuery.data.id}/profilePicture`
                    );
                }
                return updateEmployeeMutation.mutateAsync({ nextEmployee, imageFile }).then(() => undefined);
        }
        return Promise.reject(new Error("Employee data is missing"));
    };

    return (
        <Container>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 4, width: "100%", gap: 3 }}>
                {!isResourceMode && (
                    <Box sx={{ width: "100%", maxWidth: 560, display: "flex", justifyContent: "center" }}>
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<ContentCopyRoundedIcon />}
                            onClick={handleCopyBookNowLink}
                            sx={{ borderRadius: 999, px: 3, py: 1.25 }}
                        >
                            Share Your Book Now Link
                        </Button>
                    </Box>
                )}
                <ContactCard
                    firstName={employeeQuery.data.firstName}
                    lastName={employeeQuery.data.lastName}
                    email={employeeQuery.data.email || ""}
                    phone={employeeQuery.data.phone || ""}
                    bio={employeeQuery.data.bio || ""}
                    socials={employeeQuery.data.socials}
                    profileImagePath={employeeQuery.data.profilePictureImagePath}
                    profileImageRevision={profileImageRevision}
                    onSave={onSaveContactInfo}
                    isSaving={updateEmployeeMutation.isPending}
                />

                <MyServicesTable services={assignedServices} admin={employeeQuery.data.admin} />

                {/*
                <Box
                    sx={{
                        mt: 3,
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 3,
                        width: "100%",
                    }}
                >
                    <WorkHoursTable
                        title="Next 7 Days"
                        workHours={nextSevenDaysWorkHours}
                        navigateLocation={"/admin/schedule-hub"} />
                    <WorkHoursTable title={"Default Work Hours"} workHours={formattedWorkHours} navigateLocation={"/admin/edit-work-hours"} />
                </Box>
                */}
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default MyProfileScreen;
